import { Popover as MuiPopover, Stack, styled, Typography } from '@mui/material'
import { menuListStyle } from '../theme2/components/MuiMenu'
import type { PopoverProps as MuiPopoverProps } from '@mui/material'
import type { ReactNode } from 'react'

export type PopoverProps = MuiPopoverProps

/**
 * The OmUI popover shell: a surface that holds any of `PopoverHeader`, `PopoverBody` and `PopoverFooter`.
 * It restyles MUI's `Popover` paper and gives a bare `MenuList` inside it the item, divider and subheader
 * treatment MUI `Menu` applies to its own list, so a list of options renders as a menu without wrapping.
 * It owns nothing about positioning: pass `anchorEl` and `anchorOrigin` exactly as to MUI's `Popover`.
 */
export const Popover = styled(MuiPopover)(({ theme }) => ({
  '& .MuiPopover-paper': {
    padding: 0,
    borderRadius: theme.shape['radius-xl'],
    boxShadow: theme.customShadows.md,
    borderWidth: theme.strokeWidth.default,
    borderStyle: 'solid',
    borderColor: theme.palette.border.subtle,
    backgroundColor: theme.palette.bg.surface.elevated,
    backgroundImage: 'none',
  },
  '& .MuiList-root[role="menu"]': menuListStyle(theme),
}))

const PopoverHeaderRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  // Same inset as PopoverBody, so the title lines up with the content beneath it.
  padding: theme.spacing(3),
  borderBottom: `${theme.strokeWidth.default} solid ${theme.palette.border.subtle}`,
}))

export type PopoverHeaderProps = {
  title: ReactNode
  subtitle?: ReactNode
  /** Sits at the right-hand end of the header, e.g. a close or overflow button. */
  action?: ReactNode
}

export const PopoverHeader = ({ title, subtitle, action }: PopoverHeaderProps) => (
  <PopoverHeaderRoot>
    <Stack sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="display-h4" noWrap>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="small-regular" color="text.muted" noWrap>
          {subtitle}
        </Typography>
      )}
    </Stack>
    {action}
  </PopoverHeaderRoot>
)

export const PopoverBody = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
}))

export const PopoverFooter = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
  padding: theme.spacing(3, 4),
  borderTop: `${theme.strokeWidth.default} solid ${theme.palette.border.subtle}`,
}))
